import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Sidebar from '../components/layout/Sidebar';
import RightBar from '../components/layout/RightBar';
import PostCard from '../components/posts/PostCard';
import CommentList from '../components/comments/CommentList';
import Loader from '../components/common/Loader';
import Button from '../components/common/Button';
import { getPostById } from '../api/posts';

/**
 * Post page for displaying a single post and its comments
 * @returns {JSX.Element} Post page component
 */
const Post = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [apiResponse, setApiResponse] = useState(null);
  
  // Fetch post data
  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError('');
        
        console.log('Fetching post with ID:', postId);
        const response = await getPostById(postId);
        console.log('API Response:', response);
        setApiResponse(response);
        
        // Check which response format we're getting
        if (response.data && response.data.data) {
          // Handle both formats of response
          if (response.data.data.post) {
            setPost(response.data.data.post);
            
            // Check if comments are in separate field or in post
            if (response.data.data.comments) {
              setComments(response.data.data.comments);
            } else if (response.data.data.post.comments) {
              setComments(response.data.data.post.comments);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching post:', error);
        setError('Failed to load post. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    if (postId) {
      fetchPost();
    }
  }, [postId]);
  
  // Handle post/comment changes
  const handlePostUpdate = (updatedPost) => {
    setPost(updatedPost);
  };
  
  // Handle back navigation
  const handleBackClick = () => {
    navigate(-1);
  };
  
  // Render loading state
  if (loading) {
    return (
      <div className="bg-gray-100 min-h-screen">
        {/* <Header /> */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center py-12">
            <Loader size="lg" />
          </div>
        </div>
      </div>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <div className="bg-gray-100 min-h-screen">
        {/* <Header /> */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
            <div className="mt-2">
              <details>
                <summary className="cursor-pointer text-sm">Show details</summary>
                <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40">
                  {JSON.stringify(apiResponse, null, 2)}
                </pre>
              </details>
            </div>
          </div>
          <div className="mt-4">
            <Button variant="primary" onClick={handleBackClick}>
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // Render post not found
  if (!post) {
    return (
      <div className="bg-gray-100 min-h-screen">
        {/* <Header /> */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">Post not found</h3>
            <p className="mt-1 text-sm text-gray-500">
              The post you're looking for doesn't exist or has been removed.
            </p>
            <div className="mt-6">
              <Button variant="primary" onClick={handleBackClick}>
                Go Back
              </Button>
            </div>
            <div className="mt-4">
              <details>
                <summary className="cursor-pointer text-sm">Show details</summary>
                <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40">
                  {JSON.stringify(apiResponse, null, 2)}
                </pre>
              </details>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-gray-100 min-h-screen">
      {/* <Header /> */}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Left sidebar */}
          
          
          {/* Main content */}
          <div className="flex-1 space-y-6">
            {/* Back button */}
            <div>
              <button
                onClick={handleBackClick}
                className="inline-flex items-center text-gray-500 hover:text-gray-700"
              >
                <svg
                  className="h-5 w-5 mr-1"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Back
              </button>
            </div>
            
            {/* Post */}
            <PostCard post={post} showActions={true} />
            
            {/* Comments */}
            <CommentList 
              postId={post._id} 
              allowComments={true} 
              initialComments={comments} 
            />
          </div>
          
          {/* Right sidebar */}
          <div className="hidden lg:block lg:w-80">
            <RightBar />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Post;